import { usePaymentTaskFixedAssets } from '@/hooks/api/1150_payment_task_fixed_assets';
import { PaymentTaskLogic } from '@/hooks/api/models/logics';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPaymentTask } from '@/providers/PaymentTaskProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { isBackofficeURL } from '@/utils';
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

export const FixedAssets = ({ disabled }: { disabled: boolean }) => {
  const location = useLocation();
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { purchase_order_id } = useParams();
  const { paymentTask: task } = useCurrentPaymentTask();
  const { data: paymentTaskFixedAssets } = usePaymentTaskFixedAssets({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    purchase_order_id: Number(purchase_order_id),
    task_id: task!.id,
  });
  const [editPagePath, setEditPagePath] = useState<string>('');

  useEffect(() => {
    if (isBackofficeURL(location.pathname)) {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/backoffice/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${
            task!.id
          }/payment_task_fixed_assets`
      );
    } else {
      setEditPagePath(
        (_) =>
          `/w/${_w_!.identifier}/teams/${_t_!.id}/purchase_orders/${purchase_order_id}/payment_tasks/${
            task!.id
          }/payment_task_fixed_assets`
      );
    }
  }, [location.pathname]);

  const [categorizedAmount, setCategorizedAmount] = useState<number>(0);
  const [unCategorizedAmount, setUnCategorizedAmount] = useState<number>(0);
  const [stateClass, setStateClass] = useState<string>('checklist-item-secondary');

  useEffect(() => {
    if (paymentTaskFixedAssets) {
      const _categorizedAmount = PaymentTaskLogic.categorizedAmount(task!, paymentTaskFixedAssets);
      const _unCategorizedAmount = PaymentTaskLogic.unCategorizedAmount(task!, paymentTaskFixedAssets);
      setCategorizedAmount((_) => _categorizedAmount);
      setUnCategorizedAmount((_) => _unCategorizedAmount);
      setStateClass((_) => {
        if (_categorizedAmount == 0) {
          return 'checklist-item-secondary';
        } else if (_unCategorizedAmount == 0) {
          return 'checklist-item-success';
        } else {
          return 'checklist-item-danger';
        }
      });
    }
  }, [task, paymentTaskFixedAssets]);

  // 請求金額確定までは非表示
  if (task!.payment_amount == null) return <></>;

  if (paymentTaskFixedAssets == null) return <></>;

  return (
    <div className={`checklist-item ps-2 ms-3 ${stateClass}`}>
      <div className="ms-1 d-flex align-items-center">
        <div className="flex-1">
          <h6 className="mb-0 text-dark font-weight-bold text-sm">固定資産金額の投入</h6>
          <div className="text-weak text-xs">支払金額のうち、固定資産と固定資産外費用を分類してください。</div>
        </div>
        <Link
          to={editPagePath}
          className={`btn btn-outline-success btn-icon-only mb-0 mt-3 flex-shrink-0 ${disabled && 'disabled'}`}
        >
          <i className="fa fa-edit"></i>
        </Link>
      </div>
      <div className="ms-1 mt-3 ps-1">
        <div className="d-flex justify-content-end">
          <div className="table-responsive">
            <table className="table align-items-center">
              <thead>
                <tr>
                  <th
                    className="text-secondary text-xxs font-weight-bolder opacity-7 px-2 text-end"
                    style={{ width: 150 }}
                  >
                    投入金額合計
                  </th>
                  <th
                    className="text-secondary text-xxs font-weight-bold opacity-7 px-2 text-end"
                    style={{ width: 150 }}
                  >
                    未分類金額合計
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-end text-dark text-xs">¥{categorizedAmount.toLocaleString()}</td>
                  <td className="text-end text-dark text-xs">¥{unCategorizedAmount.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="table-responsive w-100">
          <table className="table align-items-center">
            <thead>
              <tr>
                <th className="text-secondary text-xxs font-weight-bolder opacity-7 px-2">資産分類</th>
                <th className="text-secondary text-xxs font-weight-bold opacity-7 px-2 text-end" style={{ width: 150 }}>
                  金額
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="d-flex align-items-center">
                    <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
                      <i className="fas fa-yen-sign text-white"></i>
                    </div>
                    <div className="ms-2">
                      <div className="font-weight-bold px-0 text-sm text-dark mb-1">固定資産外費用</div>
                    </div>
                  </div>
                </td>
                <td className="text-end text-dark text-xs">¥{task!.non_fixed_asset_amount.toLocaleString()}</td>
              </tr>
              {paymentTaskFixedAssets.map((d) => (
                <tr key={`ptfa-${d.id}`}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="icon icon-sm bg-dark d-flex border-radius-md align-items-center justify-content-center flex-shrink-0">
                        <i className="fas fa-cubes text-white"></i>
                      </div>
                      <div className="ms-2">
                        <div className="font-weight-bold px-0 text-sm text-dark mb-1">{d.fixed_asset_outline.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-end text-dark text-xs">¥{d.amount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
